/**
 * BNPL ("Udskyd betalingen") cart accordion.
 *
 * The rows behave as a single-select group: only one product can be selected at
 * a time. Checking a row removes the previously selected product from the cart
 * and adds the newly selected one; unchecking a row removes its product.
 * After each change the cart drawer is refreshed via the theme's
 * `theme:cart:refresh` event (handled by the CartItems component).
 *
 * Listeners are delegated on `document` so they keep working even though the
 * accordion lives in the cart foot, which is not re-rendered on cart updates.
 * Because of that, the checkboxes are re-synced from /cart.js on every
 * `theme:cart:change` — covering BNPL products removed via the normal
 * line-item remove button, which would otherwise leave a checkbox stuck.
 */
(function () {
  const routes = (window.theme && window.theme.routes) || {};
  const ADD_URL = routes.cart_add_url || '/cart/add';
  const CHANGE_URL = routes.cart_change_url || '/cart/change';
  const UPDATE_URL = routes.cart_update_url || '/cart/update';

  // True while an accordion-initiated add/remove is in flight, so the
  // cart-change sync below doesn't fight the optimistic checkbox state.
  let busy = false;

  function refreshCart() {
    document.dispatchEvent(new CustomEvent('theme:cart:refresh', { bubbles: true }));
  }

  function group() {
    return Array.from(document.querySelectorAll('[data-bnpl-checkbox]'));
  }

  function setGroupLoading(isLoading) {
    group().forEach((cb) => {
      cb.disabled = isLoading;
      const row = cb.closest('[data-bnpl-row]');
      if (row) row.classList.toggle('is-loading', isLoading);
    });
  }

  function addToCart(variantId) {
    return fetch(ADD_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: [{ id: Number(variantId), quantity: 1 }] }),
    }).then((response) => {
      if (!response.ok) throw new Error('Unable to add BNPL product to cart');
      return response.json();
    });
  }

  function removeFromCart(variantId) {
    return fetch(CHANGE_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ id: String(variantId), quantity: 0 }),
    }).then((response) => {
      if (!response.ok) {
        // 404 = the variant is no longer in the cart (e.g. it was removed via
        // the line-item remove button) — already the end state a removal wants.
        if (response.status === 404) return null;
        throw new Error('Unable to remove BNPL product from cart');
      }
      return response.json();
    });
  }

  // Re-aligns every checkbox with what is actually in the cart. Needed because
  // the accordion lives in the cart foot, which the theme does NOT re-render on
  // cart updates — so removing a BNPL product via its line-item remove button
  // would otherwise leave its checkbox stuck checked.
  function syncFromCart() {
    if (busy) return;

    fetch('/cart.js', { headers: { Accept: 'application/json' } })
      .then((response) => (response.ok ? response.json() : null))
      .then((cart) => {
        if (!cart || busy) return;

        const inCart = new Set(cart.items.map((item) => String(item.variant_id)));
        let anySelected = false;

        group().forEach((cb) => {
          const selected = inCart.has(cb.getAttribute('data-variant-id'));
          cb.checked = selected;
          if (selected) anySelected = true;
        });

        // BNPL product removed outside the accordion: clear the order flag so
        // the order isn't tagged/captured as BNPL without a BNPL product.
        if (!anySelected && cart.attributes && cart.attributes._bnpl === 'true') {
          updateBnplAttribute('false');
        }
      })
      .catch(() => {});
  }

  document.addEventListener('theme:cart:change', syncFromCart);

  // Flags the cart as a BNPL order (`_bnpl: 'true'`) — or clears the flag —
  // directly from the theme. The whole downstream pipeline (order tagging,
  // the capture backend, and the fulfilment Flow that must SKIP BNPL orders)
  // keys off this attribute, so it must be set even if the checkout UI
  // extension never renders (e.g. accelerated wallet checkouts). The checkout
  // extension keeps the same attribute in sync from its side.
  // Non-fatal by design: a failure here is logged but doesn't revert the cart
  // change, since the checkout extension provides a second chance to set it.
  function updateBnplAttribute(value) {
    return fetch(UPDATE_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ attributes: { _bnpl: value } }),
    })
      .then((response) => {
        if (!response.ok) throw new Error('Unable to update BNPL cart attribute');
        return response.json();
      })
      .catch((error) => {
        console.error('[bnpl] attribute update failed', error);
      });
  }

  document.addEventListener('change', function (event) {
    const checkbox = event.target.closest('[data-bnpl-checkbox]');
    if (!checkbox) return;

    const variantId = checkbox.getAttribute('data-variant-id');
    if (!variantId) return;

    let failed = false;

    if (checkbox.checked) {
      // Single-select: any other selected rows are the previous selection.
      const previous = group().filter((cb) => cb !== checkbox && cb.checked);
      previous.forEach((cb) => {
        cb.checked = false;
      });

      busy = true;
      setGroupLoading(true);

      // Remove the previous selection(s), then add the new product.
      Promise.all(previous.map((cb) => removeFromCart(cb.getAttribute('data-variant-id'))))
        .then(() => addToCart(variantId))
        .then(() => updateBnplAttribute('true'))
        .then(refreshCart)
        .catch(function (error) {
          console.error('[bnpl]', error);
          failed = true;
        })
        .finally(function () {
          busy = false;
          setGroupLoading(false);
          // On failure, realign the UI with the actual cart instead of
          // guessing — the optimistic state may be wrong in either direction.
          if (failed) syncFromCart();
        });
    } else {
      busy = true;
      setGroupLoading(true);

      removeFromCart(variantId)
        .then(() => updateBnplAttribute('false'))
        .then(refreshCart)
        .catch(function (error) {
          console.error('[bnpl]', error);
          failed = true;
        })
        .finally(function () {
          busy = false;
          setGroupLoading(false);
          if (failed) syncFromCart();
        });
    }
  });
})();
